import { motion } from "framer-motion";
import { Lightning, Drop, Cloud, CheckCircle, NavigationArrow, Warning } from "@phosphor-icons/react";

// "Sponge vs Popcorn" mode tones — palette tuned to read clearly on the
// app's locked cream background.
const MODE_TONE = {
  convective: {
    label: "CONVECTIVE BURST",
    color: "#b45309",        // amber-700
    bg: "rgba(254, 243, 199, 0.85)", // amber-100/85 (frosted)
    border: "rgba(217, 119, 6, 0.35)",
    Icon: Lightning,
  },
  stratiform: {
    label: "STRATIFORM DRIZZLE",
    color: "#3730a3",        // indigo-800
    bg: "rgba(224, 231, 255, 0.85)",
    border: "rgba(79, 70, 229, 0.35)",
    Icon: Drop,
  },
  mixed: {
    label: "MIXED PATTERN",
    color: "#475569",        // slate-700
    bg: "rgba(241, 245, 249, 0.9)",
    border: "rgba(100, 116, 139, 0.4)",
    Icon: Cloud,
  },
  clear: {
    label: "CLEAR SKIES",
    color: "#047857",        // emerald-700
    bg: "rgba(220, 252, 231, 0.85)",
    border: "rgba(16, 185, 129, 0.35)",
    Icon: CheckCircle,
  },
};

const MOTION_TONE = {
  fast: {
    color: "#be123c",        // rose-700
    bg: "rgba(254, 226, 226, 0.92)",
    border: "rgba(225, 29, 72, 0.4)",
  },
  stall: {
    color: "#9a3412",        // orange-800
    bg: "rgba(255, 237, 213, 0.92)",
    border: "rgba(234, 88, 12, 0.4)",
  },
};

/**
 * RainModePill — surfaces the dual-path engine's rain mode classification
 * (Convective / Stratiform / Mixed / Clear) as a single pill. Optionally
 * pairs with a Motion alert pill ("FAST MOVING" / "STALLING") when the
 * confidence engine detects hazard-grade cell velocity.
 *
 * Used on Page 1 (above the verdict headline) and Page 3 (above the
 * AI nowcast). On Page 1 the pills appear stacked above the ring; on
 * Page 3 they sit at the top of the AI Intelligence card.
 */
export default function RainModePill({ mode, motion: motionAlert, size = "md" }) {
  const tone = MODE_TONE[mode] || MODE_TONE.clear;
  const ModeIcon = tone.Icon;
  const compact = size === "sm";

  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap justify-center" data-testid="rain-mode-pill-row">
      <motion.div
        className={[
          "inline-flex items-center gap-1.5 rounded-full border",
          compact ? "px-2 py-0.5" : "px-2.5 py-1",
        ].join(" ")}
        style={{
          background: tone.bg,
          borderColor: tone.border,
          color: tone.color,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        }}
        initial={{ opacity: 0, scale: 0.92, y: -4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 22, delay: 0.12 }}
        data-testid="rain-mode-pill"
      >
        <ModeIcon size={compact ? 10 : 11} weight="duotone" />
        <span
          className={[
            "font-mono font-bold uppercase tracking-[0.18em]",
            compact ? "text-[9px]" : "text-[10px]",
          ].join(" ")}
        >
          {tone.label}
        </span>
      </motion.div>

      {motionAlert && MOTION_TONE[motionAlert.kind] && (
        <motion.div
          className={[
            "inline-flex items-center gap-1 rounded-full border",
            compact ? "px-1.5 py-0.5" : "px-2 py-0.5",
          ].join(" ")}
          style={{
            background: MOTION_TONE[motionAlert.kind].bg,
            borderColor: MOTION_TONE[motionAlert.kind].border,
            color: MOTION_TONE[motionAlert.kind].color,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 380, damping: 22, delay: 0.32 }}
          data-testid="motion-alert-pill"
          title={motionAlert.advice}
        >
          {/* Direction arrow — rotated to point in the storm's travel direction.
              Phosphor NavigationArrow points UP at 0°, which equals "moving N",
              so we rotate by toward_deg directly. */}
          <NavigationArrow
            size={compact ? 9 : 10}
            weight="fill"
            style={{
              transform: `rotate(${motionAlert.toward_deg ?? 0}deg)`,
              transition: "transform 600ms ease",
            }}
          />
          {motionAlert.toward_compass ? (
            <span
              className={[
                "font-mono font-black uppercase tracking-tight italic",
                compact ? "text-[9px]" : "text-[9.5px]",
              ].join(" ")}
            >
              MOVING {motionAlert.toward_compass}
            </span>
          ) : (
            <span
              className={[
                "font-mono font-black uppercase tracking-tight italic",
                compact ? "text-[9px]" : "text-[9.5px]",
              ].join(" ")}
            >
              {motionAlert.label}
            </span>
          )}
          <span
            className={[
              "font-mono tabular-nums opacity-80",
              compact ? "text-[8.5px]" : "text-[9px]",
            ].join(" ")}
          >
            {motionAlert.speed_kmh} km/h
          </span>
        </motion.div>
      )}
    </div>
  );
}
